//// This file contains 'errors' that we can safely store in the redux store (regular errors are classes and should NOT be stored in redux store)

import { FormValidationErrorType } from "./form-errors";
import { CustomizationObjectType, DicomRule, ValidationStatus } from "./customization-types";

/** Severity level for form validation errors. */
export enum FormValidationErrorSeverity {
    /** Blocks saving and shows red error icon in sidebar. */
    Error = 'Error',
    /** Does not block saving. Shows orange warning in form fields but is not propagated to the sidebar tree view. */
    Warning = 'Warning',
}

/** A model customization validation error from backend with information to identify it in UI. Uses the ViewModel suffix
 * to disambiguate it from the BackendValidationError type.
 */
export type BackendValidationErrorViewModel = {
    /** The Redux store object ID for the object that this error is about (e.g. roiId for CustomizationRois) */
    id: string;

    /** Type of the errored object. */
    type: CustomizationObjectType;

    /** A short message about the error. */
    message: string;

    /** A longer, more detailed message about the error. */
    detail: string;

    /** The field of the object that this error is about. */
    field?: string;

    /** More optional context for the error. */
    ctx?: string;
}

/** Base object for UI validation errors. 
 * 
 * TODO: this type doesn't derive from Error so it probably shouldn't have the Error suffix.
 */
export type FormValidationError = {
    /** The Redux store object ID for the object that this error is about (e.g. roiId for CustomizationRois) */
    id: string;

    /** The Redux store object ID for the object that this error is about (e.g. roiId for CustomizationRois). A business item may have
     * multiple warnings or errors -- they are represented as individual FormValidationError objects.
     */
    itemId: string;

    /** Type of the errored contouring object. */
    itemType: CustomizationObjectType;

    /** Type of this error, such as duplicate names in same customization.*/
    errorType: FormValidationErrorType;

    /** A short message about the error. */
    message?: string;

    /** A longer, more detailed message about the error. */
    detail?: string;

    /** The field of the object that this error is about. */
    field?: string;

    /** More optional context for the error. */
    ctx?: string;

    /** Severity level: 'Error' blocks saving, 'Warning' does not. */
    severity: FormValidationErrorSeverity;
}

/** A daemon validation error from backend with information to identify it in UI.
 *
 * TODO: this type doesn't derive from Error so it probably shouldn't have the Error suffix.
 */
export type DaemonValidationError = {
    /** The ID of this error, usually matching the daemon configuration item this errors is for. */
    id: string;

    /** A short message about the error. */
    message: string;

    /** A longer, more detailed message about the error. */
    detail: string;

    /** The field of the object that this error is about. */
    field?: string;

    /** More optional context for the error. */
    ctx?: string;
}

/** Returns true if any of the given form validation errors have error-level severity. */
export const hasBlockingFormValidationErrors = (errors: FormValidationError[]): boolean =>
    errors.some(e => e.severity === FormValidationErrorSeverity.Error);

/** Returns the worst severity from a list of form validation errors as a ValidationStatus enum value. */
export const getFormErrorsWorstSeverity = (errors: FormValidationError[]): ValidationStatus => {
    if (errors.some(e => e.severity === FormValidationErrorSeverity.Error)) { return ValidationStatus.Error; }
    if (errors.length > 0) { return ValidationStatus.Warning; }
    return ValidationStatus.Valid;
}

/** Computes a ValidationStatus from backend error presence and optional form errors. */
export const computeValidationStatus = (hasBackendErrors: boolean, formErrors?: FormValidationError[]): ValidationStatus => {
    if (hasBackendErrors) { return ValidationStatus.Error; }
    if (formErrors && formErrors.length > 0) { return getFormErrorsWorstSeverity(formErrors); }
    return ValidationStatus.Valid;
};

/** Collects all DICOM attribute rule IDs from a set of DICOM rules. */
export const collectDicomAttributeIds = (dicomRuleIds: string[], dicomRuleEntities: Record<string, DicomRule | undefined>): string[] => {
    const ids: string[] = [];
    for (const dId of dicomRuleIds) {
        const rule = dicomRuleEntities[dId];
        if (rule) {
            ids.push(...rule.dicomAttributes);
        }
    }
    return ids;
};

/** Filters form validation errors to those relevant to trigger rules (AE title, DICOM, DICOM attribute rules). */
export const filterTriggerFormErrors = (
    formErrors: FormValidationError[],
    aeTitleRuleIds: string[],
    dicomRuleIds: string[],
    dicomAttributeIds: string[],
): FormValidationError[] =>
    formErrors.filter(e =>
        aeTitleRuleIds.includes(e.itemId)
        || dicomRuleIds.includes(e.itemId)
        || (e.itemType === CustomizationObjectType.DicomAttributeRule && dicomAttributeIds.includes(e.itemId))
    );
